import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext"; // <--- IMPORTANTE
import { fetchAndCacheInstitutionalStudents } from "../utils/institutionalClassStudents";

const InstitutionDataContext = createContext();

// Cache TTL (1 año)
const INSTITUTION_CACHE_TTL = 365 * 24 * 60 * 60 * 1000; // ms
const CACHE_KEY = "institution_data";

export function InstitutionProvider({ children }) {
  const { user, refreshUser } = useAuth(); // <--- Ahora usamos el usuario y refreshUser
  const [institution, setInstitution] = useState(null);
  const [loading, setLoading] = useState(true);

  // Función para leer Firestore y guardar cache
  const fetchInstitution = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, "institution", "datos"), navigator.onLine ? { source: 'server' } : undefined);
      if (snap.exists()) {
        const data = snap.data();
        const lastUpdateSeconds = data.lastUpdate?.seconds || null;
        
        // Validar coherencia con usuario
        if (user && user.cachedAt && lastUpdateSeconds) {
          if (lastUpdateSeconds > (user.cachedAt / 1000)) {
            refreshUser();
          }
        }

        const toStore = { ...data, __cachedAt: Date.now(), __lastUpdateSeconds: lastUpdateSeconds };
        window.localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
        setInstitution(toStore);
      }
      } catch {
      // si falla, mantenemos institution como null o lo que haya
    } finally {
      setLoading(false);
    }
  }, [user, refreshUser]);

  // Guardián Global del Directorio Maestro
  useEffect(() => {
    if (!user) return; // Solo sincronizar si hay sesión activa

    const unsub = onSnapshot(doc(db, 'directorio_institucional', 'metadata'), async (metaSnap) => {
      if (metaSnap.exists()) {
        const serverVersion = metaSnap.data().lastUpdate || 0;
        const cachedVersion = localStorage.getItem('directorio_maestro_version');
        const localVersion = cachedVersion ? parseInt(cachedVersion, 10) : 0;
        const hasCachedData = localStorage.getItem('directorio_maestro') !== null;

        // Si la versión en la nube es mayor, o si no tenemos datos, descargamos
        if (serverVersion > localVersion || !hasCachedData) {
          try {
            console.log("Guardián: Detectada nueva versión del Directorio Maestro. Descargando...");
            const docSnap = await getDoc(doc(db, 'directorio_institucional', 'general'));
            if (docSnap.exists()) {
              const studentsArray = Object.values(docSnap.data());
              localStorage.setItem('directorio_maestro', JSON.stringify(studentsArray));
              localStorage.setItem('directorio_maestro_version', serverVersion.toString());
              
              // Avisar a cualquier componente que lo esté mostrando en vivo
              window.dispatchEvent(new Event('directorio_maestro_updated'));
              console.log("Guardián: Directorio Maestro actualizado exitosamente en caché.");
            } else {
              localStorage.removeItem('directorio_maestro');
              localStorage.removeItem('directorio_maestro_version');
              window.dispatchEvent(new Event('directorio_maestro_updated'));
            }
          } catch (err) {
            console.error("Guardián: Error sincronizando Directorio Maestro en background:", err);
          }
        }
      }
    });

    return () => unsub();
  }, [user]);

  useEffect(() => {
    // Consulta los datos institucionales con cache local
    const localRaw = window.localStorage.getItem(CACHE_KEY);
    if (localRaw) {
      let parsed = null;
      try {
        parsed = JSON.parse(localRaw);
        setInstitution(parsed);
      } catch {
        setInstitution(null);
      }

      // Si el cache aún no expiró (TTL 1 año), lo usamos
      // Pero si es muy viejo o el usuario es más nuevo que el cache, refrescamos en background
      const cachedAt = parsed?.__cachedAt || null;
      const isExpired = !cachedAt || (Date.now() - cachedAt) >= INSTITUTION_CACHE_TTL;
      
      if (!isExpired) {
        // Validación rápida de coherencia con usuario
        if (user && user.cachedAt && parsed?.__lastUpdateSeconds) {
          if (parsed.__lastUpdateSeconds > (user.cachedAt / 1000)) {
            refreshUser();
          }
        }
        setLoading(false);
        // Si no está vencido ni hay desincronización con usuario, no forzar background inmediato
        return;
      }

      // Si llegó aquí es porque expiró o necesitamos refrescar
      fetchInstitution();
    } else {
      // No hay cache local
      fetchInstitution();
    }
  }, [user, refreshUser, fetchInstitution]);

  // Actualizar datos y localStorage: ahora escribimos lastUpdate = serverTimestamp()
  const updateInstitution = useCallback(async (newData) => {
    setLoading(true);
    try {
      // 1. Escribimos merge con lastUpdate para la data institucional
      await setDoc(doc(db, "institution", "datos"), { ...newData, lastUpdate: serverTimestamp() }, { merge: true });

      // 2. Incrementamos la versión global para invalidar caches de todos los clientes (Costo 1 escritura adicional)
      // Usamos serverTimestamp() como versión para que sea siempre incremental y única
      await setDoc(doc(db, "clases_institucionales", "metadata"), { 
        inst_ver: Date.now(), 
        lastUpdate: serverTimestamp() 
      }, { merge: true });

      // Leer el doc actualizado para obtener el lastUpdate real y guardarlo en cache local
      try {
        const snap = await getDoc(doc(db, "institution", "datos"));
        if (snap.exists()) {
          const data = snap.data();
          const toStore = { ...data, __cachedAt: Date.now(), __lastUpdateSeconds: data.lastUpdate?.seconds || null };
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
          setInstitution(toStore);
        }
      } catch {
        setInstitution(newData);
      }
    } catch (err) {
      console.error("Error updating institution:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // --- EL GUARDIÁN REACTIVO (Sincronización en Tiempo Real de Configuración y Clases) ---
  useEffect(() => {
    // Solo suscribir si hay usuario logueado y estamos online
    if (!user || !navigator.onLine) return;

    console.log("[Guardian] Iniciando suscripción activa a metadatos globales...");
    
    // Suscribirse al documento metadata para detectar cambios de versión instantáneamente
    const metaRef = doc(db, "clases_institucionales", "metadata");
    
    const unsubscribe = onSnapshot(metaRef, async (metaSnap) => {
      if (!metaSnap.exists()) return;
      
      const data = metaSnap.data();
      const remoteInstVer = Number(data.inst_ver || 0);
      const versionsMap = data.versions || {};
      
      // 1. Validar Configuración Institucional (Caché de Config)
      const localInstVer = Number(localStorage.getItem("global_inst_ver") || 0);
      
      if (remoteInstVer > localInstVer) {
        console.log("[Guardian] ¡Configuración institucional actualizada! Refrescando datos...");
        localStorage.setItem("global_inst_ver", String(remoteInstVer));
        // Disparamos la carga manual inmediata
        fetchInstitution();
      }

      // 2. Sincronización Proactiva de Listas de Estudiantes (Segundo Plano)
      const classIds = Object.keys(versionsMap);
      if (classIds.length > 0) {
        for (const classId of classIds) {
          const remoteVer = versionsMap[classId];
          const localVer = Number(localStorage.getItem(`instclass_version_${classId}`) || 0);

          if (remoteVer > localVer) {
            // El utilitario ya maneja el almacenamiento en caché
            fetchAndCacheInstitutionalStudents(classId, remoteVer);
          }
        }
      }
    }, (err) => {
      console.warn("[Guardian] Error en suscripción a metadatos", err);
    });

    return () => {
      console.log("[Guardian] Cerrando suscripción activa.");
      unsubscribe();
    };
  }, [user, fetchInstitution]);

  // Permite limpiar (ejemplo: admin cambia info y quiere forzar reload)
  const clearInstitutionCache = useCallback(() => {
    window.localStorage.removeItem(CACHE_KEY);
    setInstitution(null);
    setLoading(true);
  }, []);

  return (
    <InstitutionDataContext.Provider
      value={{ institution, loading, updateInstitution, clearInstitutionCache }}
    >
      {children}
    </InstitutionDataContext.Provider>
  );
}

export function useInstitutionData() {
  return useContext(InstitutionDataContext);
}